import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';

const Notifications = ({ notifications: initialNotifications, onClose }) => {
  const [notifications, setNotifications] = useState(initialNotifications || []);
  const [loading, setLoading] = useState(!initialNotifications);
  const [error, setError] = useState('');
  const [processingNotifications, setProcessingNotifications] = useState(new Set());
  const [acceptedNotifications, setAcceptedNotifications] = useState(new Set());
  const [ignoredNotifications, setIgnoredNotifications] = useState(new Set());

  useEffect(() => {
    // Only load if we don't have initial notifications
    if (!initialNotifications) {
      loadNotifications();
    }
  }, [initialNotifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getNotifications();
      setNotifications(response.data);
    } catch (err) {
      setError('Failed to load notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptFriendRequest = async (notification) => {
    if (processingNotifications.has(notification.id) || acceptedNotifications.has(notification.id)) {
      return; // Prevent double submission
    }
    
    try {
      setProcessingNotifications(prev => new Set(prev).add(notification.id));
      const requestId = notification.data.request_id;
      const response = await userAPI.updateFriendRequest(requestId, 'ACCEPTED');
      
      if (response.status === 200) {
        await userAPI.markNotificationsRead([notification.id]);
        setAcceptedNotifications(prev => new Set(prev).add(notification.id));
        await loadNotifications(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to accept friend request');
      console.error(err);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleIgnoreFriendRequest = async (notification) => {
    if (processingNotifications.has(notification.id) || ignoredNotifications.has(notification.id)) {
      return; // Prevent double submission
    }
    
    try {
      setProcessingNotifications(prev => new Set(prev).add(notification.id));
      const requestId = notification.data.request_id;
      const response = await userAPI.updateFriendRequest(requestId, 'REJECTED');
      
      if (response.status === 200) {
        await userAPI.markNotificationsRead([notification.id]);
        setIgnoredNotifications(prev => new Set(prev).add(notification.id));
        await loadNotifications(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to ignore friend request');
      console.error(err);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleAcceptGroupInvite = async (notification) => {
    if (processingNotifications.has(notification.id) || acceptedNotifications.has(notification.id)) {
      return; // Prevent double submission
    }
    
    try {
      setProcessingNotifications(prev => new Set(prev).add(notification.id));
      const requestId = notification.data.request_id;
      const response = await userAPI.updateGroupRequest(requestId, 'ACCEPTED');
      
      if (response.status === 200) {
        await userAPI.markNotificationsRead([notification.id]);
        setAcceptedNotifications(prev => new Set(prev).add(notification.id));
        await loadNotifications(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to accept group invitation');
      console.error(err);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleIgnoreGroupInvite = async (notification) => {
    if (processingNotifications.has(notification.id) || ignoredNotifications.has(notification.id)) {
      return; // Prevent double submission
    }
    
    try {
      setProcessingNotifications(prev => new Set(prev).add(notification.id));
      const requestId = notification.data.request_id;
      const response = await userAPI.updateGroupRequest(requestId, 'REJECTED');
      
      if (response.status === 200) {
        await userAPI.markNotificationsRead([notification.id]);
        setIgnoredNotifications(prev => new Set(prev).add(notification.id));
        await loadNotifications(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to ignore group invitation');
      console.error(err);
    } finally {
      setProcessingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const renderNotification = (notification) => {
    const isProcessing = processingNotifications.has(notification.id);
    const isAccepted = acceptedNotifications.has(notification.id);
    const isIgnored = ignoredNotifications.has(notification.id);
    
    switch (notification.type) {
      case 'FRIEND_REQUEST':
        return (
          <div key={notification.id} className="notification-item">
            <div className="notification-content">
              <span className="notification-text">
                <strong>{notification.data.from_username}</strong> wants to be friends
              </span>
              {isAccepted ? (
                <div className="notification-status" style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                  ✓ Accepted friend request from {notification.data.from_username}
                </div>
              ) : isIgnored ? (
                <div className="notification-status" style={{ color: 'var(--color-warning)', fontWeight: 'bold' }}>
                  ✗ Ignored friend request from {notification.data.from_username}
                </div>
              ) : (
                <div className="notification-actions">
                  <button
                    onClick={() => handleAcceptFriendRequest(notification)}
                    className="btn btn-primary btn-sm"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Accepting...' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleIgnoreFriendRequest(notification)}
                    className="btn btn-secondary btn-sm"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Ignoring...' : 'Ignore'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'GROUP_INVITE':
        return (
          <div key={notification.id} className="notification-item">
            <div className="notification-content">
              <span className="notification-text">
                <strong>{notification.data.from_username}</strong> invited you to join <strong>{notification.data.group_name}</strong>
              </span>
              {isAccepted ? (
                <div className="notification-status" style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                  ✓ Accepted joining group {notification.data.group_name}
                </div>
              ) : isIgnored ? (
                <div className="notification-status" style={{ color: 'var(--color-warning)', fontWeight: 'bold' }}>
                  ✗ Ignored joining group {notification.data.group_name}
                </div>
              ) : (
                <div className="notification-actions">
                  <button
                    onClick={() => handleAcceptGroupInvite(notification)}
                    className="btn btn-primary btn-sm"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Accepting...' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleIgnoreGroupInvite(notification)}
                    className="btn btn-secondary btn-sm"
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Ignoring...' : 'Ignore'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      case 'NEW_USER':
        return (
          <div key={notification.id} className="notification-item">
            <div className="notification-content">
              <span className="notification-text">
                <strong>{notification.data.username}</strong> joined!
              </span>
              <div className="notification-actions">
                <button
                  onClick={async () => {
                    try {
                      await userAPI.markNotificationsRead([notification.id]);
                      await loadNotifications();
                    } catch (err) {
                      setError('Failed to mark notification as read');
                    }
                  }}
                  className="btn btn-primary btn-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
      case 'RELEASE_NOTES':
        return (
          <div key={notification.id} className="notification-item">
            <div className="notification-content">
              <span className="notification-text">
                {notification.data.message}
              </span>
              <div className="notification-actions">
                <button
                  onClick={async () => {
                    try {
                      await userAPI.markNotificationsRead([notification.id]);
                      await loadNotifications();
                    } catch (err) {
                      setError('Failed to mark notification as read');
                    }
                  }}
                  className="btn btn-primary btn-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div key={notification.id} className="notification-item">
            <span>Unknown notification type: {notification.type}</span>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="notifications-modal">
        <div className="notifications-content">
          <div className="notifications-header">
            <h2>Notifications</h2>
            <button onClick={onClose} className="close-btn">&times;</button>
          </div>
          <div className="notifications-body">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-modal">
      <div className="notifications-content">
        <div className="notifications-header">
          <h2>Notifications</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
        <div className="notifications-body">
          {error && <div className="error-message">{error}</div>}
          {notifications.length === 0 ? (
            <div className="no-notifications">No notifications</div>
          ) : (
            <div className="notifications-list">
              {notifications.map(renderNotification)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications; 